import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getLandingPageStatistics } from '../../api/landing_api';
import { rollupLandingStatisticsByMonth } from '../../utils/landingStatistics';
import DateRangePicker from './DateRangePicker';

const calendarIcon = 'https://www.figma.com/api/mcp/asset/377e23ff-89bf-484b-80ba-d64f34c49e33';

const PLATFORM_COLORS = {
  X: '#14171A',
  Facebook: '#3B5998',
  TikTok: '#69C9D0',
  Instagram: '#833AB4',
  YouTube: '#FF0000',
};
// Language view palette from Figma node 473:4095.
const LANGUAGE_COLORS_SEQUENCE = ['#6233A9', '#B4BAFF', '#71D3FD', '#B3EEB1', '#F6A2EE', '#FD7871'];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function splitMonthYearLabel(label) {
  if (typeof label !== 'string') return { month: '', year: '' };
  const trimmed = label.trim();
  if (!trimmed) return { month: '', year: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { month: parts[0], year: '' };
  return {
    month: parts.slice(0, -1).join(' '),
    year: parts[parts.length - 1],
  };
}

function MonthYearTick({ x, y, payload }) {
  const { month, year } = splitMonthYearLabel(payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fill="#303030" fontSize={11}>
        <tspan x={0}>{month}</tspan>
        {year ? <tspan x={0} dy={12}>{year}</tspan> : null}
      </text>
    </g>
  );
}

function formatPillLabel(start, end) {
  if (!start && !end) return 'Last 12 months';
  function fmt(yyyyMm) {
    if (!yyyyMm) return '?';
    const [y, m] = yyyyMm.split('-');
    return `${MONTHS_SHORT[Number(m) - 1]} ${y}`;
  }
  return `${fmt(start)} – ${fmt(end)}`;
}

function isAllTimeRange(range) {
  return Boolean(range) && range.start == null && range.end == null;
}

function NonZeroStackTooltip({ active, payload }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const items = payload.filter((entry) => Number(entry?.value) > 0);
  if (items.length === 0) return null;
  return (
    <div
      className="recharts-default-tooltip landing-tooltip"
      style={{
        background: '#fff',
        border: '1px solid #ccc',
        padding: '10px',
        whiteSpace: 'nowrap',
      }}
    >
      <ul className="recharts-tooltip-item-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((entry) => (
          <li
            key={entry.dataKey ?? entry.name}
            className="recharts-tooltip-item"
            style={{ color: entry.color, display: 'block', paddingTop: 4, paddingBottom: 4 }}
          >
            <span className="recharts-tooltip-item-name">{entry.name}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const VerifiedContentAnalysis = ({
  prefetchedStatistics,
  prefetchedStatisticsLoading,
}) => {
  const [raw, setRaw] = useState(null);
  const [dimension, setDimension] = useState('platform');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  const pillWrapRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    function handleOutsideClick(e) {
      if (pillWrapRef.current && !pillWrapRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [pickerOpen]);

  useEffect(() => {
    let cancelled = false;

    const usePrefetch =
      prefetchedStatistics !== undefined || prefetchedStatisticsLoading !== undefined;
    if (isAllTimeRange(dateRange) && usePrefetch) {
      if (prefetchedStatisticsLoading) {
        setLoading(true);
        return undefined;
      }
      setRaw(prefetchedStatistics ?? null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    (async () => {
      try {
        const next = await getLandingPageStatistics({
          startDate: dateRange.start,
          endDate: dateRange.end,
        });
        if (cancelled) return;
        setRaw(next);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Failed to load statistics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRange, prefetchedStatistics, prefetchedStatisticsLoading]);

  const rollup = useMemo(() => {
    if (!raw) {
      return { monthData: [], categories: [], categoryTotals: {}, total: 0 };
    }
    const rollupOptions =
      dimension === 'language'
        ? { excludeFromRanking: ['None'], groupRemainingAs: 'Others' }
        : undefined;
    return rollupLandingStatisticsByMonth(raw, dimension, rollupOptions);
  }, [raw, dimension]);

  const { monthData, categories, categoryTotals, total } = rollup;

  const barTitle =
    dimension === 'platform'
      ? 'Antisemitism Activity by Platform'
      : 'Antisemitism Activity by Language';

  const showEmpty = !loading && monthData.length === 0 && !error;
  const showCharts = !loading && monthData.length > 0 && categories.length > 0;
  const blockingError = Boolean(error && !loading && monthData.length === 0);

  const getCategoryColor = (name, idx) => {
    if (dimension === 'platform') return PLATFORM_COLORS[name] || LANGUAGE_COLORS_SEQUENCE[idx] || '#717cff';
    return LANGUAGE_COLORS_SEQUENCE[idx] || '#717cff';
  };

  const pieData = categories.map((cat, idx) => ({
    name: cat,
    value: categoryTotals[cat] || 0,
    fill: getCategoryColor(cat, idx),
  }));

  return (
    <>
      {error && monthData.length > 0 && (
        <div className="landing-analysis-refresh-error" role="status">
          Unable to refresh statistics ({error})
        </div>
      )}
      <div className="landing-analysis-head">
        <h2>Verified Content Analysis</h2>
        <div className="landing-analysis-controls">
          <div className="landing-date-pill-wrap" ref={pillWrapRef}>
            <button
              type="button"
              className={`landing-date-pill${pickerOpen ? ' landing-date-pill--open' : ''}`}
              onClick={() => setPickerOpen((v) => !v)}
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
            >
              <img src={calendarIcon} alt="" aria-hidden="true" />
              <span>{formatPillLabel(dateRange.start, dateRange.end)}</span>
            </button>
            {pickerOpen && (
              <DateRangePicker
                value={dateRange}
                onChange={(range) => { setDateRange(range); }}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>
          <div className="landing-pill-group">
            <button
              type="button"
              className={`landing-pill-option landing-pill-option--toggle${dimension === 'platform' ? ' active' : ''}`}
              onClick={() => setDimension('platform')}
            >
              Platform
            </button>
            <button
              type="button"
              className={`landing-pill-option landing-pill-option--toggle${dimension === 'language' ? ' active' : ''}`}
              onClick={() => setDimension('language')}
            >
              Language
            </button>
          </div>
        </div>
      </div>
      <div className="landing-analysis-grid">
        <div className="landing-analysis-card">
          <h3>{barTitle}</h3>
          {loading && <div className="landing-analysis-chart-state">Loading statistics…</div>}
          {blockingError && <div className="landing-analysis-chart-state landing-analysis-chart-state--error">{error}</div>}
          {showEmpty && <div className="landing-analysis-chart-state">No data available for this view.</div>}
          {showCharts && (
            <div className="landing-analysis-bar-wrap">
              <ResponsiveContainer width="100%" height={289}>
                <BarChart
                  data={monthData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                  barCategoryGap={8}
                  barGap={0}
                >
                  <CartesianGrid vertical={false} stroke="#e0e1eb" />
                  <XAxis
                    dataKey="month"
                    tick={<MonthYearTick />}
                    interval={0}
                    angle={0}
                    textAnchor="middle"
                    tickLine={false}
                    axisLine={false}
                    height={56}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#303030' }}
                    ticks={[0, 85, 170, 255, 340]}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<NonZeroStackTooltip />} />
                  {categories.map((cat, idx) => (
                    <Bar
                      key={cat}
                      dataKey={cat}
                      stackId="landing"
                      fill={getCategoryColor(cat, idx)}
                      radius={cat === categories[categories.length - 1] ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <div className="landing-bar-legend">
                {categories.map((cat, idx) => (
                  <div key={cat} className="landing-analysis-legend-item">
                    <span className="dot" style={{ backgroundColor: getCategoryColor(cat, idx) }} />
                    <span>{cat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="landing-analysis-card">
          <h3>Total Data Analysis for Selected Dates</h3>
          {loading && <div className="landing-analysis-chart-state">Loading statistics…</div>}
          {blockingError && <div className="landing-analysis-chart-state landing-analysis-chart-state--error">{error}</div>}
          {showEmpty && <div className="landing-analysis-chart-state">No data available for this view.</div>}
          {showCharts && (
            <div className="landing-pie-wrap">
              <div className="landing-pie-chart-box">
                <ResponsiveContainer width={201} height={201}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={88}
                      paddingAngle={1}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="landing-pie-center" aria-hidden="true">
                  <div className="landing-pie-center-value">{total.toLocaleString()}</div>
                  <div className="landing-pie-center-label">Total Items</div>
                </div>
              </div>
              <div className="landing-pie-legend">
                {pieData.map((item) => (
                  <div className="landing-pie-legend-row" key={item.name}>
                    <div className="landing-analysis-legend-item">
                      <span className="dot" style={{ backgroundColor: item.fill }} />
                      <span>{item.name}</span>
                    </div>
                    <strong>{item.value.toLocaleString()}</strong>
                  </div>
                ))}
                <div className="landing-pie-legend-divider" />
                <div className="landing-pie-legend-row landing-pie-legend-total">
                  <span>Total content items:</span>
                  <strong>{total.toLocaleString()}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifiedContentAnalysis;
